import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Folder as FolderIcon,
  Description as FileIcon,
  Quiz as QuizIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { toast } from 'react-toastify';
import quizService from '../../services/quiz.service';
import quizContainerService from '../../services/quizContainerService';
import imageService, { Image } from '../../services/image.service';
import { registerBeforeTabChange, unregisterBeforeTabChange } from './Workshop';
import ParentDropZone from '../../components/ParentDropZone';
import MathText from '../../components/MathText';
import ThumbnailPicker from '../../components/ThumbnailPicker';
import QuizSkillsField from '../../components/QuizSkillsField';

// Sortable container component
const SortableContainer: React.FC<{
  container: any;
  onNavigate: (id: number) => void;
  onEdit: (container: any) => void;
  onDelete: (id: number) => void;
  activeItemType: 'container' | 'quiz' | null;
  isNestTarget: boolean;
  showDescription?: boolean;
}> = ({ container, onNavigate, onEdit, onDelete, activeItemType, isNestTarget, showDescription }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `container-${container.id}` });

  // The dragged item keeps its transform so it follows the pointer.
  // Non-dragged containers suppress sorting shift transforms to keep rects stable for position detection.
  const style = {
    transform: (!isDragging && activeItemType !== null) ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: 'pointer',
        border: '2px dashed',
        borderColor: isNestTarget ? 'primary.main' : 'primary.light',
        backgroundColor: isNestTarget ? 'primary.light' : isDragging ? 'action.hover' : 'inherit',
        boxShadow: isNestTarget ? '0 4px 12px rgba(25, 118, 210, 0.25)' : 'none',
      }}
    >
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', '&:active': { cursor: 'grabbing' } }}
          >
            <DragIcon fontSize="small" color="action" />
          </Box>
          {container.thumbnail_url ? (
            <Avatar src={container.thumbnail_url} variant="rounded" sx={{ width: 40, height: 40 }} />
          ) : (
            <FolderIcon color="primary" />
          )}
          <Typography
            variant="body2"
            sx={{ flex: 1, cursor: 'pointer' }}
            onClick={() => onNavigate(container.id)}
          >
            {container.title}
          </Typography>
          <IconButton size="small" onClick={() => onEdit(container)}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => onDelete(container.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        {showDescription && container.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 5 }}>
            {container.description}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

// Sortable quiz item component
const SortableQuiz: React.FC<{
  quiz: any;
  isSelected: boolean;
  onSelect: (quiz: any) => void;
  onTogglePublic: (id: number, isPublic: boolean) => void;
  onDuplicate: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ quiz, isSelected, onSelect, onTogglePublic, onDuplicate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `quiz-${quiz.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: 'pointer',
        backgroundColor: isSelected ? 'primary.light' : quiz.is_public ? 'rgba(33, 150, 243, 0.08)' : 'inherit',
        borderLeft: isSelected ? '4px solid' : quiz.is_public ? '4px solid rgba(33, 150, 243, 0.3)' : 'none',
        borderLeftColor: isSelected ? 'primary.main' : undefined,
        '&:hover': { backgroundColor: isSelected ? 'primary.light' : 'action.hover' },
      }}
      onClick={() => onSelect(quiz)}
    >
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box
            {...attributes}
            {...listeners}
            sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', '&:active': { cursor: 'grabbing' } }}
            onClick={(e) => e.stopPropagation()}
          >
            <DragIcon fontSize="small" color="action" />
          </Box>
          <FileIcon fontSize="small" color="action" />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {quiz.question}
            </Typography>
            {quiz.chapitre && (
              <Typography variant="caption" color="text.secondary">
                {quiz.chapitre}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
            <Switch
              size="small"
              checked={quiz.is_public}
              onChange={(e) => onTogglePublic(quiz.id, e.target.checked)}
              title={quiz.is_public ? 'Public' : 'Privé'}
            />
            <IconButton size="small" onClick={() => onDuplicate(quiz.id)}>
              <DuplicateIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete(quiz.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

// Per-quiz draft entry for isolated editing (prevents cross-contamination on quick switch)
interface QuizDraft {
  serverSnapshot: any;
  chapitre: string;
  question: string;
  reponse: string;
  isPublic: boolean;
}

interface QuizzesProps {
  urlContainerId?: number | null;
  urlItemId?: number | null;
  onNavChange?: (containerId: number | null, itemId: number | null, label?: string) => void;
}

const Quizzes: React.FC<QuizzesProps> = ({ urlContainerId, urlItemId, onNavChange }) => {
  const [quizzes, setQuizzes] = useState<any[]>([]);
  const [containers, setContainers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentContainer, setCurrentContainer] = useState<number | null>(null);
  const [parentContainer, setParentContainer] = useState<number | null>(null);

  // --- Draft cache: per-quiz isolated state ---
  const [activeQuizId, setActiveQuizId] = useState<number | null>(null);
  const [draftCache, setDraftCache] = useState<Map<number, QuizDraft>>(new Map());
  const draftCacheRef = useRef<Map<number, QuizDraft>>(new Map());
  const activeQuizIdRef = useRef<number | null>(null);

  useEffect(() => { draftCacheRef.current = draftCache; }, [draftCache]);
  useEffect(() => { activeQuizIdRef.current = activeQuizId; }, [activeQuizId]);

  // Sync from URL (back/forward navigation)
  useEffect(() => {
    if (urlContainerId !== undefined && urlContainerId !== currentContainer) {
      setCurrentContainer(urlContainerId);
    }
  }, [urlContainerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (urlItemId !== undefined && urlItemId !== activeQuizId) {
      setActiveQuizId(urlItemId);
    }
  }, [urlItemId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Derived values from active draft (backward-compatible)
  const activeDraft = activeQuizId !== null ? draftCache.get(activeQuizId) : undefined;
  const selectedQuiz = activeDraft?.serverSnapshot ?? null;
  const chapitre = activeDraft?.chapitre ?? '';
  const question = activeDraft?.question ?? '';
  const reponse = activeDraft?.reponse ?? '';
  const isPublic = activeDraft?.isPublic ?? false;

  // Setter wrappers
  const updateActiveDraft = useCallback(<K extends keyof QuizDraft>(field: K, value: QuizDraft[K]) => {
    setDraftCache(prev => {
      const id = activeQuizIdRef.current;
      if (id === null) return prev;
      const draft = prev.get(id);
      if (!draft) return prev;
      const next = new Map(prev);
      next.set(id, { ...draft, [field]: value });
      return next;
    });
  }, []);

  const setChapitre = useCallback((v: string) => updateActiveDraft('chapitre', v), [updateActiveDraft]);
  const setQuestion = useCallback((v: string) => updateActiveDraft('question', v), [updateActiveDraft]);
  const setReponse = useCallback((v: string) => updateActiveDraft('reponse', v), [updateActiveDraft]);
  const setIsPublic = useCallback((v: boolean) => updateActiveDraft('isPublic', v), [updateActiveDraft]);

  // setSelectedQuiz wrapper: updates serverSnapshot in draft (does NOT switch active quiz)
  const setSelectedQuiz = useCallback((quizOrNull: any | null) => {
    if (quizOrNull === null) {
      setActiveQuizId(null);
      return;
    }
    setDraftCache(prev => {
      const existing = prev.get(quizOrNull.id);
      if (!existing) return prev;
      const next = new Map(prev);
      next.set(quizOrNull.id, { ...existing, serverSnapshot: quizOrNull });
      return next;
    });
  }, []);

  // Centralized save helper: saves a specific quiz's draft by ID (reads from ref, not state)
  const saveDraftIfDirty = useCallback(async (quizId: number | null) => {
    if (quizId === null) return;
    const draft = draftCacheRef.current.get(quizId);
    if (!draft) return;
    const snapshot = draft.serverSnapshot;

    const isDirty = (
      draft.chapitre !== (snapshot.chapitre || '') ||
      draft.question !== snapshot.question ||
      draft.reponse !== snapshot.reponse ||
      draft.isPublic !== snapshot.is_public
    );
    if (!isDirty) return;

    try {
      const quizData = {
        chapitre: draft.chapitre,
        question: draft.question,
        reponse: draft.reponse,
        is_public: draft.isPublic,
        container: snapshot.container,
        tags: [],
      };
      const updated = await quizService.update(quizId, quizData);
      // Mark draft as clean
      setDraftCache(prev => {
        const current = prev.get(quizId);
        if (!current) return prev;
        const next = new Map(prev);
        next.set(quizId, { ...current, serverSnapshot: updated });
        return next;
      });
      setQuizzes(prev => prev.map(q => q.id === quizId ? updated : q));
    } catch (error) {
      console.error('Error saving quiz:', error);
    }
  }, []);

  // Creation dialog
  const [quizDialogOpen, setQuizDialogOpen] = useState(false);
  const [createType, setCreateType] = useState<'container' | 'quiz'>('quiz');
  const [newContainerTitle, setNewContainerTitle] = useState('');
  const [newContainerDescription, setNewContainerDescription] = useState('');
  const [newQuizTitle, setNewQuizTitle] = useState('');
  const [newQuizReponse, setNewQuizReponse] = useState('');
  const [showDescriptions, setShowDescriptions] = useState(false);

  // Edit container dialog
  const [editContainerDialogOpen, setEditContainerDialogOpen] = useState(false);
  const [editingContainer, setEditingContainer] = useState<any | null>(null);
  const [editContainerTitle, setEditContainerTitle] = useState('');
  const [editContainerDescription, setEditContainerDescription] = useState('');
  const [editContainerThumbnail, setEditContainerThumbnail] = useState<number | null>(null);

  // Image selection
  const [imageSelectionDialogOpen, setImageSelectionDialogOpen] = useState(false);
  const [allImages, setAllImages] = useState<Image[]>([]);

  // DnD
  const [activeItemType, setActiveItemType] = useState<'container' | 'quiz' | null>(null);
  const [nestTargetId, setNestTargetId] = useState<number | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  // Define these functions early so they can be used in useEffect dependencies
  const initialLoadDone = useRef(false);

  const loadQuizzes = useCallback(async () => {
    try {
      if (!initialLoadDone.current) setLoading(true);
      const params: any = {};
      if (currentContainer === null) {
        params.container = 'null';
      } else if (currentContainer) {
        params.container = currentContainer;
      }

      const data = await quizService.getAll(params);
      setQuizzes(data);
    } catch (error) {
      console.error('Error loading quizzes:', error);
      toast.error('Erreur lors du chargement des quizz');
    } finally {
      setLoading(false);
      initialLoadDone.current = true;
    }
  }, [currentContainer]);

  const loadContainers = useCallback(async () => {
    try {
      const data = await quizContainerService.getContainers(currentContainer);
      setContainers(data);

      // Load parent info if we're inside a container
      if (currentContainer !== null) {
        const containerInfo = await quizContainerService.getContainer(currentContainer);
        setParentContainer(containerInfo.parent);
      } else {
        setParentContainer(null);
      }
    } catch (error) {
      console.error('Error loading containers:', error);
    }
  }, [currentContainer]);

  useEffect(() => {
    loadQuizzes();
    loadContainers();
  }, [currentContainer, loadQuizzes, loadContainers]);

  // Auto-save with 2 second delay when content changes
  useEffect(() => {
    if (activeQuizId === null) return;
    const timer = setTimeout(() => {
      saveDraftIfDirty(activeQuizId);
    }, 2000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuizId, chapitre, question, reponse, isPublic, saveDraftIfDirty]);

  // Register callback to save before tab change
  useEffect(() => {
    registerBeforeTabChange('quizzes', () => saveDraftIfDirty(activeQuizIdRef.current));
    return () => { unregisterBeforeTabChange('quizzes'); };
  }, [saveDraftIfDirty]);

  // Save on component unmount
  useEffect(() => {
    return () => {
      const quizId = activeQuizIdRef.current;
      if (quizId === null) return;
      const draft = draftCacheRef.current.get(quizId);
      if (!draft) return;
      const snapshot = draft.serverSnapshot;
      const isDirty = (
        draft.chapitre !== (snapshot.chapitre || '') ||
        draft.question !== snapshot.question ||
        draft.reponse !== snapshot.reponse ||
        draft.isPublic !== snapshot.is_public
      );
      if (!isDirty) return;
      quizService.update(quizId, {
        chapitre: draft.chapitre,
        question: draft.question,
        reponse: draft.reponse,
        is_public: draft.isPublic,
        container: snapshot.container,
        tags: [],
      }).catch(err => console.error('Error saving quiz on unmount:', err));
    };
  }, []);

  const handleSelectQuiz = (quiz: any) => {
    // Fire-and-forget save of current draft (no await — instant switch)
    saveDraftIfDirty(activeQuizIdRef.current);

    const newQuizId = quiz.id;
    setActiveQuizId(newQuizId);
    onNavChange?.(currentContainer, newQuizId, quiz.chapitre || quiz.question);

    // If no draft yet, populate from the quiz data
    if (!draftCache.has(newQuizId)) {
      setDraftCache(prev => {
        if (prev.has(newQuizId)) return prev;
        const next = new Map(prev);
        next.set(newQuizId, {
          serverSnapshot: quiz,
          chapitre: quiz.chapitre || '',
          question: quiz.question,
          reponse: quiz.reponse,
          isPublic: quiz.is_public,
        });
        return next;
      });
    }
  };

  const handleNavigateToContainer = (containerId: number | null) => {
    // Fire-and-forget save of current draft
    saveDraftIfDirty(activeQuizIdRef.current);
    setCurrentContainer(containerId);
    const container = containers.find(c => c.id === containerId);
    onNavChange?.(containerId, null, container?.title);
  };

  const handleDelete = async (quizId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce quizz ?')) {
      return;
    }

    try {
      await quizService.delete(quizId);
      toast.success('Quizz supprimé');
      // Remove from draft cache
      setDraftCache(prev => {
        const next = new Map(prev);
        next.delete(quizId);
        return next;
      });
      if (activeQuizId === quizId) {
        setActiveQuizId(null);
      }
      loadQuizzes();
    } catch (error) {
      console.error('Error deleting quiz:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDuplicate = async (quizId: number) => {
    try {
      await quizService.duplicate(quizId);
      toast.success('Quizz dupliqué');
      loadQuizzes();
    } catch (error) {
      console.error('Error duplicating quiz:', error);
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleTogglePublicQuiz = async (quizId: number, newIsPublic: boolean) => {
    try {
      await quizService.update(quizId, { is_public: newIsPublic });
      toast.success(`Quizz rendu ${newIsPublic ? 'public' : 'privé'}`);

      // Update in the list
      setQuizzes(quizzes.map(q => q.id === quizId ? { ...q, is_public: newIsPublic } : q));

      // Update draft if it exists
      setDraftCache(prev => {
        const existing = prev.get(quizId);
        if (!existing) return prev;
        const next = new Map(prev);
        next.set(quizId, {
          ...existing,
          isPublic: newIsPublic,
          serverSnapshot: { ...existing.serverSnapshot, is_public: newIsPublic },
        });
        return next;
      });
    } catch (error) {
      console.error('Error updating quiz visibility:', error);
      toast.error('Erreur lors de la mise à jour de la visibilité');
    }
  };

  const handleNew = () => {
    setQuizDialogOpen(true);
    setCreateType('quiz');
  };

  const handleCreateContainer = async () => {
    if (!newContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await quizContainerService.createContainer({
        title: newContainerTitle,
        description: newContainerDescription,
        parent: currentContainer,
      });
      toast.success('Contenant créé');
      setQuizDialogOpen(false);
      setNewContainerTitle('');
      setNewContainerDescription('');
      loadContainers();
    } catch (error) {
      console.error('Error creating container:', error);
      toast.error('Erreur lors de la création du contenant');
    }
  };

  const handleCreateNewQuiz = async () => {
    if (!newQuizTitle.trim()) {
      toast.error('La question est requise');
      return;
    }

    try {
      const newQuiz = await quizService.create({
        question: newQuizTitle,
        reponse: newQuizReponse || 'À compléter',
        chapitre: '',
        is_public: false,
        container: currentContainer,
        tags: [],
      });
      toast.success('Quizz créé');
      setQuizDialogOpen(false);
      setNewQuizTitle('');
      setNewQuizReponse('');

      // Create draft and select
      setDraftCache(prev => {
        const next = new Map(prev);
        next.set(newQuiz.id, {
          serverSnapshot: newQuiz,
          chapitre: newQuiz.chapitre || '',
          question: newQuiz.question,
          reponse: newQuiz.reponse,
          isPublic: newQuiz.is_public,
        });
        return next;
      });
      setActiveQuizId(newQuiz.id);
      onNavChange?.(currentContainer, newQuiz.id, newQuiz.chapitre);
      loadQuizzes();
    } catch (error) {
      console.error('Error creating quiz:', error);
      toast.error('Erreur lors de la création du quizz');
    }
  };

  const handleEditContainer = (container: any) => {
    setEditingContainer(container);
    setEditContainerTitle(container.title);
    setEditContainerDescription(container.description || '');
    setEditContainerThumbnail(container.thumbnail || null);
    setEditContainerDialogOpen(true);
  };

  const handleUpdateContainer = async () => {
    if (!editContainerTitle.trim()) {
      toast.error('Le nom du contenant est requis');
      return;
    }

    try {
      await quizContainerService.updateContainer(editingContainer.id, {
        title: editContainerTitle,
        description: editContainerDescription,
        thumbnail: editContainerThumbnail,
      });
      toast.success('Contenant mis à jour');
      setEditContainerDialogOpen(false);
      setEditingContainer(null);
      setEditContainerTitle('');
      setEditContainerDescription('');
      setEditContainerThumbnail(null);
      loadContainers();
    } catch (error) {
      console.error('Error updating container:', error);
      toast.error('Erreur lors de la mise à jour du contenant');
    }
  };

  const handleDeleteContainer = async (containerId: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contenant ?')) {
      return;
    }

    try {
      await quizContainerService.deleteContainer(containerId);
      toast.success('Contenant supprimé');
      loadContainers();
    } catch (error) {
      console.error('Error deleting container:', error);
      toast.error('Erreur lors de la suppression du contenant');
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const activeId = event.active.id.toString();
    console.log('[Quizzes DnD] dragStart:', activeId);
    setNestTargetId(null);
    if (activeId.startsWith('container-')) {
      setActiveItemType('container');
    } else if (activeId.startsWith('quiz-')) {
      setActiveItemType('quiz');
    }
  };

  const handleDropToParent = async (itemId: string) => {
    if (itemId.startsWith('quiz-')) {
      const quizId = parseInt(itemId.replace('quiz-', ''));
      try {
        await quizService.update(quizId, { container: parentContainer });
        toast.success('Quizz déplacé');
        loadQuizzes();
      } catch (error) {
        toast.error('Erreur lors du déplacement');
      }
    } else if (itemId.startsWith('container-')) {
      const containerId = parseInt(itemId.replace('container-', ''));
      try {
        await quizContainerService.updateContainer(containerId, { parent: parentContainer });
        toast.success('Contenant déplacé');
        loadContainers();
      } catch (error) {
        toast.error('Erreur lors du déplacement du contenant');
      }
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setNestTargetId(null);
      return;
    }

    const activeId = active.id.toString();
    const overId = over.id.toString();
    const isActiveContainer = activeId.startsWith('container-');
    const isOverContainer = overId.startsWith('container-');

    // Only detect nest target when dragging a container over another container
    if (isActiveContainer && isOverContainer) {
      const overRect = over.rect;
      const activeRect = active.rect.current.translated;
      if (overRect && activeRect) {
        const activeCenterY = activeRect.top + activeRect.height / 2;
        const overTop = overRect.top;
        const overHeight = overRect.height;
        const relativeY = (activeCenterY - overTop) / overHeight;

        // Middle 40% of the target = nest, edges = reorder
        if (relativeY > 0.3 && relativeY < 0.7) {
          const overContainerId = parseInt(overId.replace('container-', ''));
          setNestTargetId(overContainerId);
          return;
        }
      }
    }

    // Also detect when dragging a quiz over a container
    if (activeId.startsWith('quiz-') && isOverContainer) {
      const overContainerId = parseInt(overId.replace('container-', ''));
      setNestTargetId(overContainerId);
      return;
    }

    setNestTargetId(null);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const currentNestTarget = nestTargetId;
    setActiveItemType(null);
    setNestTargetId(null);
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeId = active.id.toString();
    const overId = over.id.toString();

    const isActiveContainer = activeId.startsWith('container-');
    const isOverContainer = overId.startsWith('container-');
    const isActiveQuiz = activeId.startsWith('quiz-');
    const isOverParent = overId === 'parent-folder';

    // Case 1: Drop onto parent folder
    if (isOverParent) {
      await handleDropToParent(activeId);
      return;
    }

    // Case 2: Container dropped on another container
    if (isActiveContainer && isOverContainer) {
      const activeContainerId = parseInt(activeId.replace('container-', ''));
      const overContainerId = parseInt(overId.replace('container-', ''));
      if (activeContainerId === overContainerId) return;

      // If nest target was set (center zone), nest inside. Otherwise fall through to reorder.
      if (currentNestTarget === overContainerId) {
        try {
          await quizContainerService.updateContainer(activeContainerId, { parent: overContainerId });
          toast.success('Contenant imbriqué');
          loadContainers();
        } catch (error) {
          console.error('Error nesting container:', error);
          toast.error('Erreur lors du déplacement du contenant');
        }
        return;
      }
      // Fall through to Case 4 (reorder)
    }

    // Case 3: Quiz dropped on a container → move quiz into container
    if (isActiveQuiz && isOverContainer) {
      const quizId = parseInt(activeId.replace('quiz-', ''));
      const containerId = parseInt(overId.replace('container-', ''));
      try {
        await quizService.update(quizId, { container: containerId });
        toast.success('Quizz déplacé');
        loadQuizzes();
      } catch (error) {
        console.error('Error moving quiz to container:', error);
        toast.error('Erreur lors du déplacement');
      }
      return;
    }

    // Case 4: Regular reordering within the same level
    const allItems = [...containers, ...quizzes];
    const isContainerItem = (item: any) => containers.includes(item);
    const activeIndex = allItems.findIndex(item => {
      if (activeId.startsWith('container-')) {
        return isContainerItem(item) && item.id === parseInt(activeId.replace('container-', ''));
      }
      return !isContainerItem(item) && item.id === parseInt(activeId.replace('quiz-', ''));
    });
    const overIndex = allItems.findIndex(item => {
      if (overId.startsWith('container-')) {
        return isContainerItem(item) && item.id === parseInt(overId.replace('container-', ''));
      }
      return !isContainerItem(item) && item.id === parseInt(overId.replace('quiz-', ''));
    });

    if (activeIndex !== -1 && overIndex !== -1) {
      const newItems = arrayMove(allItems, activeIndex, overIndex);

      const newContainers = newItems.filter(item => isContainerItem(item));
      const newQuizzes = newItems.filter(item => !isContainerItem(item));

      setContainers(newContainers);
      setQuizzes(newQuizzes);

      // Save new order
      if (newContainers.length > 0) {
        const containerOrder = newContainers.map((item, idx) => ({ id: item.id, order: idx }));
        quizContainerService.reorderContainers(containerOrder).catch(error => {
          console.error('Error reordering containers:', error);
          loadContainers();
        });
      }
      if (newQuizzes.length > 0) {
        const quizOrder = newQuizzes.map((item, idx) => ({ id: item.id, order: idx }));
        quizService.reorder(quizOrder).catch(error => {
          console.error('Error reordering quizzes:', error);
          loadQuizzes();
        });
      }
    }
  };

  const loadAllImages = async () => {
    try {
      const images = await imageService.getAll({});
      setAllImages(images);
    } catch (error) {
      console.error('Error loading images:', error);
    }
  };

  const handleOpenImageSelection = () => {
    loadAllImages();
    setImageSelectionDialogOpen(true);
  };

  const handleAddLibraryImage = async (imageId: number) => {
    if (!selectedQuiz) return;
    try {
      const updatedQuiz = await quizService.addLibraryImage(selectedQuiz.id, imageId);
      setSelectedQuiz(updatedQuiz);
      setQuizzes(prev => prev.map(q => q.id === updatedQuiz.id ? updatedQuiz : q));
      toast.success('Image ajoutée au quizz');
    } catch (error: any) {
      console.error('Error adding image:', error);
      toast.error("Erreur lors de l'ajout de l'image");
    }
  };

  const handleRemoveLibraryImage = async (imageId: number) => {
    if (!selectedQuiz) return;
    try {
      const updatedQuiz = await quizService.removeLibraryImage(selectedQuiz.id, imageId);
      setSelectedQuiz(updatedQuiz);
      setQuizzes(prev => prev.map(q => q.id === updatedQuiz.id ? updatedQuiz : q));
      toast.success('Image retirée du quizz');
    } catch (error: any) {
      console.error('Error removing image:', error);
      toast.error("Erreur lors du retrait de l'image");
    }
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left Panel - List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 'calc(100vh - 250px)', overflow: 'auto' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">Quizz</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={handleNew}
                size="small"
              >
                Nouveau
              </Button>
            </Box>
            <Box display="flex" alignItems="center" mb={1}>
              <Switch
                size="small"
                checked={showDescriptions}
                onChange={(e) => setShowDescriptions(e.target.checked)}
              />
              <Typography variant="caption" color="text.secondary">
                Descriptions
              </Typography>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragOver={handleDragOver}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={[
                    ...(currentContainer !== null ? ['parent-folder'] : []),
                    ...containers.map(c => `container-${c.id}`),
                    ...quizzes.map(q => `quiz-${q.id}`),
                  ]}
                  strategy={verticalListSortingStrategy}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {currentContainer !== null && (
                      <ParentDropZone
                        parentContainer={null}
                        onDrop={handleDropToParent}
                        onClick={() => handleNavigateToContainer(parentContainer)}
                      />
                    )}
                    {containers.map((container) => (
                      <SortableContainer
                        key={`container-${container.id}`}
                        container={container}
                        onNavigate={handleNavigateToContainer}
                        onEdit={handleEditContainer}
                        onDelete={handleDeleteContainer}
                        activeItemType={activeItemType}
                        isNestTarget={nestTargetId === container.id}
                        showDescription={showDescriptions}
                      />
                    ))}
                    {quizzes.map((quiz) => (
                      <SortableQuiz
                        key={`quiz-${quiz.id}`}
                        quiz={quiz}
                        isSelected={activeQuizId === quiz.id}
                        onSelect={handleSelectQuiz}
                        onTogglePublic={handleTogglePublicQuiz}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                      />
                    ))}
                  </Box>
                </SortableContext>
              </DndContext>
            )}
          </Paper>
        </Grid>

        {/* Right Panel - Form */}
        <Grid item xs={12} md={8}>
          {selectedQuiz ? (
            <Paper sx={{
              p: 3,
              backgroundColor: selectedQuiz.is_public ? 'rgba(33, 150, 243, 0.08)' : 'background.paper',
              borderRadius: 2
            }}>
              <Typography variant="h6" gutterBottom>
                Modifier le Quizz
              </Typography>

              <TextField
                fullWidth
                label="Chapitre (optionnel)"
                value={chapitre}
                onChange={(e) => setChapitre(e.target.value)}
                margin="normal"
              />

              {activeQuizId !== null && (
                <QuizSkillsField
                  quizId={activeQuizId}
                  skills={selectedQuiz?.skills ?? []}
                  onSaved={(skills) => setSelectedQuiz({ ...selectedQuiz, skills })}
                />
              )}

              <TextField
                fullWidth
                label="Question"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                margin="normal"
                multiline
                rows={3}
                required
                helperText="Utilisez $...$ pour les formules (ex: $\frac{1}{2}$)"
              />
              {question && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, mb: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Aperçu</Typography>
                  <Typography variant="body1"><MathText text={question} format={selectedQuiz?.format} /></Typography>
                </Paper>
              )}

              <TextField
                fullWidth
                label="Réponse"
                value={reponse}
                onChange={(e) => setReponse(e.target.value)}
                margin="normal"
                multiline
                rows={5}
                required
                helperText="Utilisez $...$ pour les formules (ex: $\frac{1}{2}$)"
              />
              {reponse && (
                <Paper variant="outlined" sx={{ p: 1.5, mt: 0.5, mb: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>Aperçu</Typography>
                  <Typography variant="body1"><MathText text={reponse} format={selectedQuiz?.reponse_format} /></Typography>
                </Paper>
              )}

              {/* Library Images Section */}
              <Box sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="subtitle2">Images</Typography>
                  <Button size="small" startIcon={<ImageIcon />} onClick={handleOpenImageSelection}>
                    Gérer les images
                  </Button>
                </Box>
                {selectedQuiz?.library_images_details && selectedQuiz.library_images_details.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1,
                      overflowX: 'auto',
                      py: 1,
                      '&::-webkit-scrollbar': { height: '8px' },
                      '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '4px' },
                    }}
                  >
                    {selectedQuiz.library_images_details.map((img: any) => (
                      <Box
                        key={img.id}
                        sx={{
                          position: 'relative',
                          minWidth: '120px',
                          minHeight: '100px',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          overflow: 'hidden',
                          '&:hover .delete-btn': { opacity: 1 },
                        }}
                      >
                        {img.thumbnail_url || img.image_file_url ? (
                          <Box
                            component="img"
                            src={img.thumbnail_url || img.image_file_url || ''}
                            alt={img.title}
                            sx={{ width: '120px', height: '100px', objectFit: 'cover' }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: '120px',
                              height: '100px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'action.hover',
                            }}
                          >
                            <ImageIcon />
                          </Box>
                        )}
                        <IconButton
                          className="delete-btn"
                          size="small"
                          onClick={() => handleRemoveLibraryImage(img.id)}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            bgcolor: 'background.paper',
                            opacity: 0,
                            transition: 'opacity 0.2s',
                            '&:hover': { bgcolor: 'error.main', color: 'error.contrastText' },
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            p: 0.5,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              fontSize: '0.65rem',
                            }}
                          >
                            {img.title}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    Aucune image associée
                  </Typography>
                )}
              </Box>

            </Paper>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography variant="body1" color="text.secondary">
                Sélectionnez un quizz pour le modifier ou créez-en un nouveau
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Creation Dialog */}
      <Dialog open={quizDialogOpen} onClose={() => setQuizDialogOpen(false)}>
        <DialogTitle>Nouveau</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={createType}
              exclusive
              onChange={(e, newValue) => newValue && setCreateType(newValue)}
              fullWidth
            >
              <ToggleButton value="container">
                <FolderIcon sx={{ mr: 1 }} />
                Contenant
              </ToggleButton>
              <ToggleButton value="quiz">
                <QuizIcon sx={{ mr: 1 }} />
                Nouveau quizz
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {createType === 'container' && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Nom du contenant"
                fullWidth
                variant="outlined"
                value={newContainerTitle}
                onChange={(e) => setNewContainerTitle(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateContainer();
                  }
                }}
              />
              <TextField
                margin="dense"
                label="Description (optionnel)"
                fullWidth
                variant="outlined"
                multiline
                rows={2}
                value={newContainerDescription}
                onChange={(e) => setNewContainerDescription(e.target.value)}
              />
            </>
          )}
          {createType === 'quiz' && (
            <>
              <TextField
                autoFocus
                margin="dense"
                label="Question du quizz"
                fullWidth
                variant="outlined"
                value={newQuizTitle}
                onChange={(e) => setNewQuizTitle(e.target.value)}
              />
              <TextField
                margin="dense"
                label="Réponse (optionnel)"
                fullWidth
                variant="outlined"
                value={newQuizReponse}
                onChange={(e) => setNewQuizReponse(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newQuizTitle.trim()) {
                    handleCreateNewQuiz();
                  }
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setQuizDialogOpen(false);
            setNewContainerTitle('');
            setNewContainerDescription('');
            setNewQuizTitle('');
          }}>Annuler</Button>
          {createType === 'container' && (
            <Button variant="contained" onClick={handleCreateContainer}>Créer</Button>
          )}
          {createType === 'quiz' && (
            <Button variant="contained" onClick={handleCreateNewQuiz}>Créer</Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Container Dialog */}
      <Dialog open={editContainerDialogOpen} onClose={() => setEditContainerDialogOpen(false)}>
        <DialogTitle>Modifier le contenant</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nom du contenant"
            fullWidth
            variant="outlined"
            value={editContainerTitle}
            onChange={(e) => setEditContainerTitle(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && editContainerTitle.trim()) {
                handleUpdateContainer();
              }
            }}
          />
          <TextField
            margin="dense"
            label="Description (optionnel)"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={editContainerDescription}
            onChange={(e) => setEditContainerDescription(e.target.value)}
          />
          <ThumbnailPicker
            value={editContainerThumbnail}
            onChange={setEditContainerThumbnail}
            currentThumbnailUrl={editingContainer?.thumbnail_url}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditContainerDialogOpen(false);
            setEditingContainer(null);
            setEditContainerTitle('');
            setEditContainerDescription('');
            setEditContainerThumbnail(null);
          }}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateContainer} disabled={!editContainerTitle.trim()}>Modifier</Button>
        </DialogActions>
      </Dialog>

      {/* Image Selection Dialog */}
      <Dialog
        open={imageSelectionDialogOpen}
        onClose={() => setImageSelectionDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Sélectionner des images</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {allImages.map((image) => {
              const isAdded = selectedQuiz?.library_images?.includes(image.id);
              return (
                <Grid item xs={6} sm={4} md={3} key={image.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: isAdded ? '2px solid' : '1px solid',
                      borderColor: isAdded ? 'primary.main' : 'divider',
                      position: 'relative',
                    }}
                    onClick={() => {
                      if (isAdded) {
                        handleRemoveLibraryImage(image.id);
                      } else {
                        handleAddLibraryImage(image.id);
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 120,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.hover',
                      }}
                    >
                      {image.thumbnail_url || image.image_file_url ? (
                        <Box
                          component="img"
                          src={image.thumbnail_url || image.image_file_url || ''}
                          alt={image.title}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <ImageIcon sx={{ fontSize: 40 }} />
                      )}
                    </Box>
                    <CardContent sx={{ p: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {image.title}
                      </Typography>
                    </CardContent>
                    {isAdded && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 16,
                        }}
                      >
                        &#10003;
                      </Box>
                    )}
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {allImages.length === 0 && (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              Aucune image disponible
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageSelectionDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Quizzes;
